/**
 * Billing materializer — selects and constructs exactly one runtime binding.
 */

import { resolveBillingWebhookApplicationId } from "../../billing/ingestion/application-id.ts";
import {
  type AthenaBillingModule,
  createBillingModule,
} from "../../billing/module.ts";
import {
  createBillingRuntimeFacade,
  createUnavailableBillingFacade,
} from "../../billing/runtime/facade.ts";
import {
  billingSqlFromPostgresRuntime,
  bootstrapLocalBillingRuntime,
  createLocalBillingRuntimeBinding,
  type MaterializedLocalBillingRuntime,
} from "../../billing/runtime/local/materialize.ts";
import { createBillingProviderRegistry } from "../../billing/runtime/local/providers/create-registry.ts";
import { createRemoteBillingRuntime } from "../../billing/runtime/remote/runtime.ts";
import {
  ATHENA_ENV_API_KEY_KEYS,
  ATHENA_ENV_DB_URL_KEYS,
  ATHENA_ENV_URL_KEYS,
} from "../../env/index.ts";
import { getBoundPostgresRuntime } from "../../postgres/owned-runtime.ts";
import type { AthenaClientConfig } from "../../client/contracts.ts";
import type { AthenaClientModelsInput } from "../../schema/types.ts";
import type {
  AthenaRuntimePlan,
  AthenaRuntimePlanBilling,
} from "../plan/types.ts";

export { bootstrapLocalBillingRuntime, createBillingProviderRegistry };

export type AthenaMaterializedBilling =
  | ({
    kind: "local";
    module: ReturnType<typeof createBillingRuntimeFacade>;
  } & MaterializedLocalBillingRuntime)
  | {
    kind: "remote";
    module: AthenaBillingModule;
    runtime: ReturnType<typeof createRemoteBillingRuntime>;
  }
  | {
    kind: "unavailable";
    module: ReturnType<typeof createUnavailableBillingFacade>;
  };

function remoteBillingUrl<TModels extends AthenaClientModelsInput | undefined>(
  config: AthenaClientConfig<TModels>,
  plan: Extract<AthenaRuntimePlanBilling, { kind: "remote" }>,
): string | undefined {
  const environmentValue = (keys: readonly string[]): string | undefined => {
    for (const key of keys) {
      const value = config.env?.[key]?.trim();
      if (value) {
        return value;
      }
    }
    return;
  };
  const root = config.url?.trim() || environmentValue(ATHENA_ENV_URL_KEYS);
  const database =
    config.db?.url?.trim() ||
    environmentValue(ATHENA_ENV_DB_URL_KEYS) ||
    (root ? `${root.replace(/\/+$/, "")}/db` : undefined);
  return plan.endpoint ?? config.billing?.url ?? database ?? root ?? undefined;
}

function remoteBillingApiKey<
  TModels extends AthenaClientModelsInput | undefined,
>(config: AthenaClientConfig<TModels>): string {
  if (config.key?.trim()) {
    return config.key.trim();
  }
  for (const key of ATHENA_ENV_API_KEY_KEYS) {
    const value = config.env?.[key]?.trim();
    if (value) {
      return value;
    }
  }
  return "";
}

export function materializeBilling<
  TModels extends AthenaClientModelsInput | undefined,
>(
  config: AthenaClientConfig<TModels>,
  plan: AthenaRuntimePlan,
): AthenaMaterializedBilling {
  const billing = plan.billing;
  if (billing == null) {
    return {
      kind: "unavailable",
      module: createUnavailableBillingFacade(),
    };
  }
  if (billing.kind === "local") {
    const postgres = getBoundPostgresRuntime(config.gatewayTransport);
    const binding = createLocalBillingRuntimeBinding({
      applicationId: resolveBillingWebhookApplicationId({
        app: config.app,
        client: config.client,
      }),
      appUrl: config.app?.url,
      catalog: config.billing?.catalog,
      configuredProviders: config.billing?.providers,
      customerImport: config.billing?.import,
      ingestion: config.billing?.ingestion,
      mode: config.billing?.mode,
      observability: config.billing?.observability,
      ...(postgres ? { sql: billingSqlFromPostgresRuntime(postgres) } : {}),
      selfEnrollment: config.billing?.selfEnrollment,
      testMode: config.billing?.testMode,
    });
    return {
      ...binding,
      kind: "local",
      module: createBillingRuntimeFacade(binding.runtime),
    };
  }

  if (billing.kind === "remote") {
    const baseUrl = remoteBillingUrl(config, billing);
    if (!baseUrl) {
      return {
        kind: "unavailable",
        module: createUnavailableBillingFacade(
          "Athena billing has no runnable remote transport.",
        ),
      };
    }
    const clientConfig = {
      apiKey: remoteBillingApiKey(config),
      baseUrl,
      client: config.client,
      headers: {
        ...(config.headers ?? {}),
        ...(config.billing?.headers ?? {}),
      },
    };
    const runtime = createRemoteBillingRuntime(clientConfig);
    const compatibility = createBillingModule(clientConfig, runtime);
    return {
      kind: "remote",
      module: createBillingRuntimeFacade(runtime, compatibility),
      runtime,
    };
  }

  if (billing.kind === "unavailable") {
    return {
      kind: "unavailable",
      module: createUnavailableBillingFacade(),
    };
  }

  const _exhaustive: never = billing;
  return _exhaustive;
}
